import React, { useState } from 'react';
import {
  Bell,
  User,
  Package,
  ShoppingCart,
  TrendingDown,
  AlertCircle,
  CreditCard,
  Settings,
  Megaphone,
  // Loader2,
} from 'lucide-react';
import { HiOutlineBell } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import {
  useGetMyNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkNotificationsReadMutation,
} from '../../app/services/notificationApi';
import Loader from '../../Components/Ui/Loader';

/**
 * Map notification type to appropriate Lucide icon
 */
const getNotificationIcon = (type) => {
  const iconMap = {
    new_user: User,
    new_order: ShoppingCart,
    order_update: ShoppingCart,
    new_product: Package,
    product_update: Package,
    price_change: TrendingDown,
    low_stock: AlertCircle,
    payment_received: CreditCard,
    payment_pending: CreditCard,
    system: Settings,
    announcement: Megaphone,
  };
  return iconMap[type] || Bell;
};

/**
 * Format relative time from ISO date string
 */
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
};

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  // Fetch notifications and unread count
  const {
    data: notificationsData,
    isLoading,
    error,
  } = useGetMyNotificationsQuery({ limit: 5 }, { skip: !isOpen });
  const { data: unreadData } = useGetUnreadCountQuery();
  const [markNotificationsRead] = useMarkNotificationsReadMutation();

  const notifications = notificationsData?.data || [];
  const unreadCount = unreadData?.data?.unreadCount || 0;

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.isRead) {
      try {
        await markNotificationsRead({ notificationIds: [notification._id] });
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }
    // Navigate to action URL if present
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    setIsOpen(false);
  };

  const handleViewAllClick = () => {
    setIsOpen(false);
    navigate('/seller/notifications');
  };

  return (
    <div className="relative notification-container">
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-1 rounded-full text-gray-600 hover:text-primary transition-colors"
      >
        <HiOutlineBell className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 primary-background white-text rounded-full w-[18px] h-[18px] text-xs font-semibold flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          {/* Dropdown Content */}
          <div className="absolute right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 w-80 max-h-[400px] overflow-hidden z-50">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800">Notifications</h3>
              {unreadCount > 0 && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {unreadCount} new notification{unreadCount !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {/* Notifications List */}
            <div className="max-h-64 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader />
                </div>
              ) : error ? (
                <div className="p-4 text-center text-red-500 text-sm">
                  Failed to load notifications
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <Bell size={28} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No notifications yet</p>
                </div>
              ) : (
                notifications.map((notification) => {
                  const IconComponent = getNotificationIcon(notification.type);
                  return (
                    <div
                      key={notification._id}
                      className={`p-3 border-b border-gray-100 cursor-pointer transition-colors duration-200 flex items-start gap-3 ${
                        !notification.isRead ? 'bg-orange-50' : 'bg-white hover:bg-gray-50'
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div
                        className={`${
                          !notification.isRead ? 'primary-background' : 'bg-gray-400'
                        } rounded-full p-1.5 flex items-center justify-center min-w-7 h-7`}
                      >
                        <IconComponent size={14} color="white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start mb-0.5">
                          <h4
                            className={`text-sm ${
                              !notification.isRead ? 'font-semibold' : 'font-medium'
                            } text-gray-800 leading-tight`}
                          >
                            {notification.title}
                          </h4>
                          {!notification.isRead && (
                            <div className="w-2 h-2 primary-background rounded-full ml-2 mt-1" />
                          )}
                        </div>
                        <p className="text-xs text-gray-600 leading-snug mb-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <span className="text-xs text-gray-400">
                          {formatRelativeTime(notification.createdAt)}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
              <button
                onClick={handleViewAllClick}
                className="w-full text-primary text-sm font-medium py-1 hover:underline"
              >
                View All Notifications
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationDropdown;
